import { Fragment, useCallback, useId, useRef } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { startOfDay, startOfTomorrow } from 'date-fns';
import { type FormikHelpers, useFormikContext } from 'formik';

import type { ApiError } from '@monetr/interface/api/client';
import { Button } from '@monetr/interface/components/Button';
import FormAmountField from '@monetr/interface/components/FormAmountField';
import FormButton from '@monetr/interface/components/FormButton';
import FormDatePicker from '@monetr/interface/components/FormDatePicker';
import FormTextField from '@monetr/interface/components/FormTextField';
import MForm from '@monetr/interface/components/MForm';
import MModal, { type MModalRef } from '@monetr/interface/components/MModal';
import MSelectFrequency from '@monetr/interface/components/MSelectFrequency';
import { Switch } from '@monetr/interface/components/Switch';
import Typography from '@monetr/interface/components/Typography';
import { useCreateFundingSchedule } from '@monetr/interface/hooks/useCreateFundingSchedule';
import { useCurrentLink } from '@monetr/interface/hooks/useCurrentLink';
import useLocaleCurrency from '@monetr/interface/hooks/useLocaleCurrency';
import { useSelectedBankAccountId } from '@monetr/interface/hooks/useSelectedBankAccountId';
import useTimezone from '@monetr/interface/hooks/useTimezone';
import type FundingSchedule from '@monetr/interface/models/FundingSchedule';
import type { APIError } from '@monetr/interface/util/request';
import { useSnackbar } from '@monetr/notify';

import styles from './NewFundingModal.module.scss';

interface NewFundingValues {
  name: string;
  nextOccurrence: Date;
  ruleset: string;
  excludeWeekends: boolean;
  estimatedDeposit?: number | null;
  autoCreateTransaction: boolean;
}

function NewFundingModal(): React.JSX.Element {
  const switchId = useId();
  const { inTimezone } = useTimezone();
  const modal = useModal();
  const ref = useRef<MModalRef>(null);
  const { enqueueSnackbar } = useSnackbar();
  const selectedBankAccountId = useSelectedBankAccountId();
  const createFundingSchedule = useCreateFundingSchedule();
  const { data: link } = useCurrentLink();
  const isManual = Boolean(link?.getIsManual());
  const { data: locale } = useLocaleCurrency();

  const initialValues: NewFundingValues = {
    name: '',
    nextOccurrence: startOfTomorrow({
      in: inTimezone,
    }),
    ruleset: '',
    excludeWeekends: false,
    estimatedDeposit: undefined,
    autoCreateTransaction: false,
  };

  const submit = useCallback(
    async (values: NewFundingValues, helpers: FormikHelpers<NewFundingValues>): Promise<void> => {
      if (!selectedBankAccountId || !locale) {
        return Promise.resolve();
      }

      helpers.setSubmitting(true);
      const estimatedDeposit = values.estimatedDeposit ?? 0;
      return await createFundingSchedule({
        bankAccountId: selectedBankAccountId,
        name: values.name,
        description: null,
        nextRecurrence: startOfDay(new Date(values.nextOccurrence), {
          in: inTimezone,
        }),
        ruleset: values.ruleset,
        estimatedDeposit: estimatedDeposit > 0 ? locale.friendlyToAmount(estimatedDeposit) : null,
        excludeWeekends: values.excludeWeekends,
        // Auto create transaction requires a manual link and a non-zero
        // estimated deposit; force it off otherwise so the API will not reject
        // the create.
        autoCreateTransaction: isManual && estimatedDeposit > 0 && values.autoCreateTransaction,
      })
        .then(created => modal.resolve(created))
        .then(() => modal.remove())
        .catch(
          (error: ApiError<APIError>) =>
            void enqueueSnackbar(error.response.data.error, {
              variant: 'error',
              disableWindowBlurListener: true,
            }),
        )
        .finally(() => helpers.setSubmitting(false));
    },
    [createFundingSchedule, enqueueSnackbar, locale, modal, selectedBankAccountId, inTimezone, isManual],
  );

  return (
    <MModal className={styles.modal} open={modal.visible} ref={ref}>
      <MForm className={styles.form} data-testid='new-funding-modal' initialValues={initialValues} onSubmit={submit}>
        {({ setFieldValue, values }) => (
          <Fragment>
            <div className={styles.body}>
              <Typography className={styles.heading} size='xl' weight='bold'>
                Create A New Funding Schedule
              </Typography>
              <FormTextField
                autoComplete='off'
                autoFocus
                data-1p-ignore
                label='What do you want to call your funding schedule?'
                name='name'
                placeholder='Example: Payday...'
                required
              />
              <FormDatePicker
                label='When do you get paid next?'
                min={startOfTomorrow({
                  in: inTimezone,
                })}
                name='nextOccurrence'
                required
              />
              <MSelectFrequency
                dateFrom='nextOccurrence'
                label='How often do you get paid?'
                name='ruleset'
                placeholder='Select a funding frequency...'
                required
              />
              <FormAmountField
                allowNegative={false}
                label='Estimated Deposit'
                name='estimatedDeposit'
                placeholder='Example: $ 1,000.00'
              />
              <div className={styles.optionRow}>
                <div className={styles.optionText}>
                  <label className={styles.optionLabel} htmlFor={switchId}>
                    Exclude Weekends
                  </label>
                  <p className={styles.optionDescription}>
                    If it were to land on a weekend, it is adjusted to the previous weekday instead.
                  </p>
                </div>
                <Switch
                  checked={values.excludeWeekends}
                  id={switchId}
                  onCheckedChange={() => setFieldValue('excludeWeekends', !values.excludeWeekends)}
                />
              </div>
              {isManual && <AutoCreateTransactionToggle />}
            </div>
            <div className={styles.actions}>
              <Button data-testid='close-new-funding-modal' onClick={modal.remove} variant='secondary'>
                Cancel
              </Button>
              <FormButton type='submit' variant='primary'>
                Create
              </FormButton>
            </div>
          </Fragment>
        )}
      </MForm>
    </MModal>
  );
}

function AutoCreateTransactionToggle(): React.JSX.Element {
  const autoCreateSwitchId = useId();
  const { setFieldValue, values } = useFormikContext<NewFundingValues>();
  const hasDeposit = (values.estimatedDeposit ?? 0) > 0;

  return (
    <div className={styles.optionRow} data-testid='new-funding-auto-create-transaction'>
      <div className={styles.optionText}>
        <label aria-disabled={!hasDeposit} className={styles.optionLabel} htmlFor={autoCreateSwitchId}>
          Auto create transaction
        </label>
        <p aria-disabled={!hasDeposit} className={styles.optionDescription}>
          Automatically add a deposit transaction for the estimated deposit each time the funding schedule would occur.
        </p>
      </div>
      <Switch
        checked={hasDeposit && values.autoCreateTransaction}
        disabled={!hasDeposit}
        id={autoCreateSwitchId}
        onCheckedChange={() => setFieldValue('autoCreateTransaction', !values.autoCreateTransaction)}
      />
    </div>
  );
}

const newFundingModal = NiceModal.create(NewFundingModal);

export default newFundingModal;

export function showNewFundingModal(): Promise<FundingSchedule | null> {
  return NiceModal.show(newFundingModal) as Promise<FundingSchedule | null>;
}
